import PropTypes from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Merges two flat arrays (today + yesterday) by index into [{label, today, yesterday}]
const mergeSeriesData = (data, compareData, valueKey, compareKey, labelKey) =>
  data.map((d, i) => ({
    [labelKey]: d[labelKey],
    today: d[valueKey] ?? 0,
    yesterday: compareData[i]?.[compareKey] ?? 0,
  }));

const CountingChart = ({
  data = [],
  compareData = [],
  valueKey = 'totalCount',
  compareKey = 'totalCount',
  labelKey = 'label',
  title,
  subtitle,
  height = 200,
  variant = 'area',
  todayLabel = 'Hoje',
  yesterdayLabel = 'Ontem',
  color,
  showLegend = true,
}) => {
  const theme = useTheme();
  const primaryColor = color || theme.palette.primary.main;
  const compareColor = theme.palette.text.disabled || '#BDBDBD';

  // ── Sparkline — tiny chart, no axes, no tooltip, no legend
  if (variant === 'sparkline') {
    const chartData = data.length === 1
      ? [{ ...data[0], [valueKey]: 0 }, data[0]]
      : data;
    const gradId = `sparkGrad-${valueKey}-${primaryColor.replace('#', '')}`;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
          <defs>
            <linearGradient
id={gradId}
x1="0"
y1="0"
x2="0"
y2="1"
            >
              <stop
offset="5%"
stopColor={primaryColor}
stopOpacity={0.35}
              />
              <stop
offset="95%"
stopColor={primaryColor}
stopOpacity={0.02}
              />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey={valueKey}
            stroke={primaryColor}
            strokeWidth={1.5}
            fill={`url(#${gradId})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // ── Area chart — dual series (today vs yesterday)
  const merged = compareData.length
    ? mergeSeriesData(data, compareData, valueKey, compareKey, labelKey)
    : data.map(d => ({ [labelKey]: d[labelKey], today: d[valueKey] ?? 0 }));

  const hasTwoSeries = compareData.length > 0;

  if (!merged.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography
variant="body2"
color="text.secondary"
        >
          Sem dados disponíveis
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {(title || (showLegend && hasTwoSeries)) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
          <Box>
            {title && (
              <Typography
variant="subtitle2"
fontWeight={600}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
variant="caption"
color="text.secondary"
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {showLegend && hasTwoSeries && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 18, height: 2, bgcolor: primaryColor, borderRadius: 1 }} />
                <Typography
variant="caption"
color="text.secondary"
                >
                  {todayLabel}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {/* Dashed line representation */}
                <Box sx={{ display: 'flex', gap: '2px' }}>
                  <Box sx={{ width: 5, height: 2, bgcolor: compareColor }} />
                  <Box sx={{ width: 5, height: 2, bgcolor: compareColor }} />
                  <Box sx={{ width: 5, height: 2, bgcolor: compareColor }} />
                </Box>
                <Typography
variant="caption"
color="text.secondary"
                >
                  {yesterdayLabel}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
      <ResponsiveContainer
width="100%"
height={height}
      >
        <AreaChart
data={merged}
margin={{ top: 5, right: 5, left: -24, bottom: 0 }}
        >
          <defs>
            <linearGradient
id="todayAreaGrad"
x1="0"
y1="0"
x2="0"
y2="1"
            >
              <stop
offset="5%"
stopColor={primaryColor}
stopOpacity={0.25}
              />
              <stop
offset="95%"
stopColor={primaryColor}
stopOpacity={0.02}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={theme.palette.divider}
            vertical={false}
          />
          <XAxis
            dataKey={labelKey}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
            interval={2}
          />
          <YAxis
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 4,
              fontSize: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            }}
            labelStyle={{ fontWeight: 600, color: theme.palette.text.primary }}
          />
          <Area
            type="monotone"
            dataKey="today"
            name={todayLabel}
            stroke={primaryColor}
            strokeWidth={2}
            fill="url(#todayAreaGrad)"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
          {hasTwoSeries && (
            <Area
              type="monotone"
              dataKey="yesterday"
              name={yesterdayLabel}
              stroke={compareColor}
              strokeWidth={1.5}
              fill="none"
              strokeDasharray="5 4"
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

CountingChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  compareData: PropTypes.arrayOf(PropTypes.object),
  valueKey: PropTypes.string,
  compareKey: PropTypes.string,
  labelKey: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  height: PropTypes.number,
  variant: PropTypes.oneOf(['area', 'sparkline']),
  todayLabel: PropTypes.string,
  yesterdayLabel: PropTypes.string,
  color: PropTypes.string,
  showLegend: PropTypes.bool,
};

export default CountingChart;
